import posthog from "posthog-js";

export interface Rewardful {
  referral?: string;
}

export function getRewardfulClient() {
  return (window as any).Rewardful as Rewardful;
}

// ============================================================
// Facebook Ads Attribution Debugging
// ============================================================

export interface FacebookAttributionData {
  // Facebook Click ID - primary attribution parameter
  fbclid: string | null;

  // UTM parameters
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;

  // Additional FB parameters
  fb_source: string | null;

  // Referrer info
  referrer: string;
  isFromFacebook: boolean;

  // Page info
  landingPage: string;
  fullUrl: string;

  // Timing
  timestamp: string;

  // All URL parameters (for debugging unknown params)
  allParams: Record<string, string>;
}

/**
 * Captures and logs all Facebook attribution data from the current URL
 */
export function captureFacebookAttribution(): FacebookAttributionData {
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer;

  // Extract all URL parameters
  const allParams: Record<string, string> = {};
  params.forEach((value, key) => {
    allParams[key] = value;
  });

  // Check if referrer is from Facebook
  const facebookDomains = [
    'facebook.com',
    'fb.com',
    'fb.me',
    'm.facebook.com',
    'l.facebook.com',
    'lm.facebook.com',
    'instagram.com',  // Instagram is owned by Meta/FB
  ];

  const utmSource = params.get('utm_source')?.toLowerCase() ?? '';
  const isFromFacebook =
    facebookDomains.some(domain => referrer.includes(domain)) ||
    params.has('fbclid') ||
    utmSource.includes('facebook') ||
    utmSource.includes('fb') ||
    utmSource.includes('instagram') ||
    utmSource.includes('meta');

  const data: FacebookAttributionData = {
    // Facebook Click ID
    fbclid: params.get('fbclid'),

    // UTM parameters
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term'),

    // Additional FB parameters
    fb_source: params.get('fb_source'),

    // Referrer info
    referrer,
    isFromFacebook,

    // Page info
    landingPage: window.location.pathname,
    fullUrl: window.location.href,

    // Timing
    timestamp: new Date().toISOString(),

    // All params for debugging
    allParams,
  };

  return data;
}

/**
 * Logs Facebook attribution data to console and tracking systems
 */
export function logFacebookAttribution(): FacebookAttributionData | null {
  const data = captureFacebookAttribution();

  // Anything worth attributing? Not just Meta — Google/Reddit UTMs too.
  const hasAttribution = Boolean(
    data.fbclid || data.utm_source || data.utm_campaign || data.utm_content
    || data.allParams.gclid,
  );

  // If from Facebook, send to tracking systems
  if (data.isFromFacebook) {
    console.log('FB attribution captured debugging...');

    // Send to PostHog
    posthog.capture('fb_attribution_captured', {
      fbclid: data.fbclid,
      utm_source: data.utm_source,
      utm_medium: data.utm_medium,
      utm_campaign: data.utm_campaign,
      utm_content: data.utm_content,
      utm_term: data.utm_term,
      fb_source: data.fb_source,
      referrer: data.referrer,
      landing_page: data.landingPage,
    });
  }

  // Store first-party for signup: sessionStorage (this visit) + a first-touch
  // localStorage copy that survives the session for later-day converters.
  // No cookies, no third parties — read back at onboarding save and written
  // to the users row (per-ad DB attribution).
  if (hasAttribution) {
    try {
      sessionStorage.setItem('fb_attribution', JSON.stringify(data));
      if (!localStorage.getItem('first_attribution')) {
        localStorage.setItem('first_attribution', JSON.stringify(data));
      }
    } catch (e) {
      console.warn('Failed to store attribution:', e);
    }
    return data;
  }

  return null;
}

/**
 * Helper to retrieve stored Facebook attribution data
 * Useful when user signs up/converts to attach attribution to the conversion
 */
export function getStoredFacebookAttribution(): FacebookAttributionData | null {
  try {
    // This visit's touch first, then the device's first touch (older session).
    const stored = sessionStorage.getItem('fb_attribution')
      ?? localStorage.getItem('first_attribution');
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    console.warn('Failed to retrieve FB attribution from sessionStorage:', e);
    return null;
  }
}
